import { ChevronRight, KeyRound, LogOut, ShieldCheck, ShieldQuestion } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useSession } from '@/components/auth/session';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { SettingRow } from '@/components/ui/segmented';
import { changePassword } from '@/lib/auth';
import { PASSWORD_MIN_LENGTH, passwordProblem } from '@/lib/password';

/**
 * How the hub knows who you are, said out loud.
 *
 * This sentence used to be the `title` of a chip on the identity card, which is
 * the wrong place for it twice over: a tooltip is unreadable on a touch screen,
 * and the difference between an identity the hub verified and a name this
 * browser handed it is the whole basis of what the audit log means. It is a
 * claim about trust, so it belongs as visible text in the region about trust.
 *
 * Only a signed-in account reaches this component today, so the self-reported
 * case is a fallback sentence rather than a second design: one paragraph saying
 * which promise is in force, and how to get the stronger one.
 */
function SignedInRow() {
  const { session } = useSession();
  const verified = session.identity === 'session';
  const address = session.user?.email || session.actor;
  const how = verified
    ? `The hub verified this account and signed you in as ${address}. The audit log names this address rather than a header, so every job opened and every plan approved under it is yours.`
    : `This is the admin token plus the name this browser sends, “${session.actor}”. The hub is repeating a string rather than naming an account it checked. Sign in with an account and the hub names you instead.`;
  const standing = session.isAdmin
    ? 'An admin on this hub, so the fleet, people and adapter pages are drawn for you.'
    : `A ${session.role} on this hub, so the admin pages are not drawn for you.`;

  return (
    <div className="flex items-start gap-3 px-gutter py-3.5">
      {verified ? (
        <ShieldCheck className="text-ok mt-0.5 size-4 shrink-0" aria-hidden />
      ) : (
        <ShieldQuestion className="text-warn mt-0.5 size-4 shrink-0" aria-hidden />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-base font-medium">How you are signed in</p>
        <p className="text-fg-2 mt-0.5 text-sm">{how}</p>
        <p className="text-fg-3 mt-1 text-xs">{standing}</p>
      </div>
    </div>
  );
}

/** Why the fields below are not optional today. */
function TemporaryPasswordNote() {
  return (
    <div className="flex items-start gap-3 border-b px-gutter py-3.5">
      <KeyRound className="text-warn mt-0.5 size-4 shrink-0" aria-hidden />
      <p className="text-fg-2 text-sm">
        This account is on a temporary password: an admin set it, or it is the one typed when this
        hub was first set up. Choose your own and the rest of the console opens.
      </p>
    </div>
  );
}

/**
 * Security: how you got in, the password, and the way out.
 *
 * The password form (docs/JOB-LIFECYCLE.md section 2.9) is folded away behind a
 * native `details`, because on every day but one it is a form nobody needs and
 * as an always-open card it was taking a screen's worth of the page above
 * everything a person actually came here for. Closed it is one row, with no
 * state to hold and no script to run.
 *
 * `required` is that one day: an admin set a temporary password, or this is the
 * first admin's sign-up password, and the hub refuses every other page until it
 * is changed. Then the disclosure is forced open, the region is titled in the
 * warning colour, the reason is printed above the fields and the submit wears
 * the gate fill. Same component, unmissable when it matters, quiet when it does
 * not.
 *
 * The two checks the browser can make are made on the way to the button rather
 * than in a toast afterwards: the length rule mirrors the hub's, the confirm has
 * to match, and the current password is the hub's to verify. Success and failure
 * are both said next to the fields they are about.
 */
export function SecuritySection({ required }: { required: boolean }) {
  const { session, signOut, refresh } = useSession();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Said as the person types, once both fields have something in them, so a
  // mismatch is discovered before the button rather than after it.
  const liveProblem = next && confirm ? passwordProblem(next, confirm) : null;
  const disclosureHint = required
    ? 'required before the rest of the console opens'
    : 'signs out every other session on this account';

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await changePassword(current, next, confirm);
      setCurrent('');
      setNext('');
      setConfirm('');
      setDone(true);
      // The hub cleared the temporary-password flag on the way through; the
      // session has to learn that before the shell will let the person leave.
      await refresh();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'that did not work');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section>
      <SectionHead
        title="Security"
        hue={required ? 'var(--dy-warn)' : undefined}
        meta={required ? 'this comes first' : 'this account, and the way out of it'}
      />
      <Surface>
        <div className="divide-line divide-y">
          <SignedInRow />

          {/* `open` is passed only while the password is temporary. The rest of
              the time this is an ordinary uncontrolled disclosure, which is what
              keeps a submit from slamming it shut and leaves the success line
              under the fields that produced it. */}
          <details className="group" open={required || undefined}>
            <summary className="marker:content-[''] flex cursor-pointer list-none items-center gap-2 px-gutter py-[calc(var(--dy-row-py)+1px)]">
              <ChevronRight
                className="text-fg-3 size-3 shrink-0 transition-transform duration-100 group-open:rotate-90"
                aria-hidden
              />
              <span className="text-base font-medium">Change password</span>
              <span className="text-fg-3 min-w-0 truncate text-xs">{disclosureHint}</span>
            </summary>
            <div className="border-t">
              {required ? <TemporaryPasswordNote /> : null}
              <form className="grid gap-3 px-gutter py-4 sm:max-w-[26rem]" onSubmit={onSubmit}>
                <div className="space-y-1.5">
                  <Label htmlFor="current-password">Current password</Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={current}
                    autoComplete="current-password"
                    onChange={(event) => setCurrent(event.target.value)}
                    required
                    className="h-8"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="new-password">New password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={next}
                    autoComplete="new-password"
                    onChange={(event) => setNext(event.target.value)}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    className="h-8"
                  />
                  <p className="text-fg-3 text-2xs">At least {PASSWORD_MIN_LENGTH} characters.</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password">New password again</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirm}
                    autoComplete="new-password"
                    onChange={(event) => setConfirm(event.target.value)}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    aria-invalid={liveProblem ? true : undefined}
                    className="h-8"
                  />
                </div>
                {liveProblem ? (
                  <p className="text-warn text-xs" role="status">
                    {liveProblem}
                  </p>
                ) : null}
                {error ? (
                  <p className="text-bad text-xs" role="alert">
                    {error}
                  </p>
                ) : null}
                {done ? (
                  <p className="text-ok text-xs" role="status">
                    Password changed. Every other session on this account was signed out.
                  </p>
                ) : null}
                <div>
                  <Button
                    type="submit"
                    size="sm"
                    variant={required ? 'gate' : 'outline'}
                    disabled={busy || Boolean(liveProblem) || !current || !next || !confirm}
                  >
                    {busy ? 'Working…' : 'Change password'}
                  </Button>
                </div>
              </form>
            </div>
          </details>

          {/* The only sign out in the console was an unlabelled icon in the
              bottom corner of the rail, and this is the page a person opens
              looking for it. It is not destructive in the sense `ui/menu.tsx`
              guards, since nothing is lost and signing back in lands on the same
              page, so there is no confirm step in front of it. */}
          <SettingRow
            title="Sign out"
            hint="Ends this session in this browser. Other sessions on the account stay open."
            control={
              <Button
                size="sm"
                variant="outline"
                onClick={() => void signOut()}
                title={`Sign out ${session.user?.email || session.actor}`}
              >
                <LogOut aria-hidden />
                Sign out
              </Button>
            }
          />
        </div>
      </Surface>
    </Section>
  );
}
